import Collection from "./collection";
import Node from "./node";

class GameTree {
  constructor() {
    this.parent = undefined; // Collection or GameTree
    this.nodes = [];
    this.children = []; // child GameTrees (variations)
  }

  /**
   * Returns the depth of this GameTree. The depth of the root GameTree 
   * (= Collection's direct child) is 0.
   */
  get depth() {
    let depth = 0;
    let parent = this.parent;
    while (parent instanceof GameTree) {
      depth++;
      parent = parent.parent;
    }
    return depth;
  }

  get firstNode() {
    return this.nodes && this.nodes.length > 0 ? this.nodes[0] : null;
  }

  get lastNode() {
    return this.nodes && this.nodes.length > 0 ? this.nodes[this.nodes.length - 1] : null;
  }

  get rootGameTree() {
    if (this.parent instanceof GameTree) {
      return this.parent.rootGameTree;
    }
    return this;
  }

  /**
   * Adds a node.
   * 
   * @param {*} node 
   * @returns this
   */
  add(node) {
    if (!(node instanceof Node)) {
      throw new TypeError('Not a Node');
    }
    this.nodes.push(node);
    node.parent = this;
    return this;
  }

  /**
   * Inserts a node at the index.
   * 
   * @param {number} index 
   * @param {Node} node 
   * @returns this
   */
  insert(index, node) {
    if (!(node instanceof Node)) {
      throw new TypeError('Not a Node');
    } else if (index < 0 || index > this.nodes.length) {
      throw new RangeError();
    }
    this.nodes.splice(index, 0, node);
    node.parent = this;
    return this;
  }

  /**
   * Returns true if contains the node.
   * 
   * @param {Node} node 
   */
  contains(node) {
    return this.nodes.indexOf(node) > -1;
  }

  /**
   * Removes the node.
   * 
   * @param {*} node 
   * @returns this
   */
  remove(node) {
    if (!(node instanceof Node)) {
      throw new TypeError('Not a Node');
    }
    this.nodes = this.nodes.filter(n => n !== node);
    node.parent = undefined;
    return this;
  }

  /**
   * Adds a child GameTree (variation).
   * 
   * @param {GameTree} gameTree 
   * @returns this
   */
  addChild(gameTree) {
    if (!(gameTree instanceof GameTree)) {
      throw new TypeError('Not a GameTree');
    }
    this.children.push(gameTree);
    gameTree.parent = this;
    return this;
  }

  /**
   * Inserts a child GameTree at the index.
   * 
   * @param {*} index 
   * @param {*} gameTree 
   * @returns this
   */
  insertChild(index, gameTree) {
    if (!(gameTree instanceof GameTree)) {
      throw new TypeError('Not a GameTree');
    } else if (index < 0 || index > this.children.length) {
      throw new RangeError();
    }
    this.children.splice(index, 0, gameTree);
    gameTree.parent = this;
    return this;
  }

  /**
   * Removes the GameTree.
   * 
   * @param {GameTree} gameTree 
   */
  removeChild(gameTree) {
    if (!(gameTree instanceof GameTree)) {
      throw new TypeError('Not a GameTree');
    }
    this.children = this.children.filter(gt => gt !== gameTree);
    gameTree.parent = undefined;
    return this;
  }

  /**
   * Returns true if this GameTree contains the specified child GameTree.
   * 
   * @param {*} gameTree 
   * @returns true if this GameTree contains the specified child GameTree
   */
  containsChild(gameTree) {
    if (!(gameTree instanceof GameTree)) {
      throw new TypeError('Not a GameTree');
    }
    return this.children.indexOf(gameTree) !== -1;
  }

  /**
   * Returns true if this GameTree has any children (variations).
   * 
   * @returns true if this GameTree has any children (variations).
   */
  hasChild() {
    return this.children.length > 0;
  }

  isEmpty() {
    return this.nodes.length === 0 && this.children.length === 0;
  }

  /**
   * Merges this GameTree to the parent.
   * 
   * @returns this
   */
  mergeToParent() {
    if (!(this.parent instanceof GameTree)) {
      return;
    }
    this.parent.nodes = this.parent.nodes.concat(this.nodes);
    this.parent.children = this.parent.children.concat(this.children);
    this.parent.removeChild(this);
    return this;
  }

  /**
   * Moves this GameTree forward in the parent's tree.
   */
  moveUp() {
    const index = this.parent.children.indexOf(this);
    if (index > 0) {
      const parent = this.parent;
      parent.removeChild(this);
      parent.insertChild(index - 1, this);
    }
  }

  /**
   * Moves this GameTree backward in the parent's tree.
   */
  moveDown() {
    const index = this.parent.children.indexOf(this);
    if (index < this.parent.children.length - 1) {
      const parent = this.parent;
      parent.removeChild(this);
      parent.insertChild(index + 1, this);
    }
  }

  isRootGameTree() {
    return this.parent instanceof Collection;
  }

  containsRootProperty() {
    return this.firstNode && this.firstNode.containsRootProperty();
  }

  clear() {
    this.nodes = [];
    this.children = [];
  }

  getDepthFirstNodeList() {
    let nodes = [].concat(this.nodes);
    this.children.forEach(child => {
      nodes = nodes.concat(child.getDepthFirstNodeList());
    });
    return nodes;
  }

  getVariationFirstNodeList() {
    let nodes = [].concat(this.nodes);
    if (this.children.length > 0) {
      const children = [].concat(this.children);
      const mainTree = children.shift();
      children.push(mainTree);
      children.forEach(child => {
        nodes = nodes.concat(child.getVariationFirstNodeList());
      });
    }
    return nodes;
  }

  toString() {
    return "("
      + this.nodes.reduce((str, node) => str + node, "")
      + this.children.reduce((str, child) => str + child, "")
      + ")";
  }
}

export default GameTree;